import { createFileRoute, Link } from "@tanstack/react-router";

import { NewMapsCountdown } from "#/components/NewMapsCountdown";
import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { PageShell } from "#/components/PageShell";
import { SectionHeader } from "#/components/SectionHeader";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "#/components/ui/card";
import { UpdatesBanner } from "#/components/UpdatesBanner";
import { DOCUMENT_TITLES, documentHead, homeJsonLd } from "#/lib/documentTitle";
import { globalStatsQueryOptions } from "#/lib/queries";
import { loadPlayIndexData } from "#/server/game/playIndex";

import { HomeChallengeSection } from "./-index.challengeSection";
import { DiscordPromoCard } from "./-index.discordPromo";
import { EarlyBirdPromoCard } from "./-index.earlyBirdPromo";
import { GlobalStatsSection } from "./-index.globalStats";
import { LearnFromTheBestSection } from "./-index.learnFromTheBest";
import { MonthlyHiscoresMarquee } from "./-index.monthlyHiscoresMarquee";
import { DailyMapColumn } from "./-play.heroAndLeaderboards";

export const Route = createFileRoute("/")({
  head: () =>
    documentHead(DOCUMENT_TITLES.home, {
      canonicalPath: "/",
      jsonLd: homeJsonLd(),
    }),
  component: HomePage,
  loader: async ({ context }) => {
    const data = await loadPlayIndexData();
    context.queryClient.setQueryData(globalStatsQueryOptions().queryKey, data.globalStats);
    return data;
  },
});

function homeWelcomeLine(isAnonymous: boolean, displayName: string | null): string {
  if (isAnonymous) return "Welcome, Commander!";
  return `Welcome, ${displayName ?? "Player"}!`;
}

function HomeDailyMapCards({ data }: { data: Awaited<ReturnType<typeof loadPlayIndexData>> }) {
  return (
    <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
      <DailyMapColumn
        title="Standard"
        mapId={data.standardMapId}
        bestDays={data.standardBestDays}
        bestReplayId={data.standardBestReplayId}
        bestMedalTier={data.standardBestMedalTier}
        leaderboardEntries={data.standardLeaderboard}
        access={data.accessByMapId[data.standardMapId] ?? { ok: true }}
        dailyStreak={data.standardDailyStreak}
      />
      <DailyMapColumn
        title="Premium"
        mapId={data.premiumMapId}
        bestDays={data.premiumBestDays}
        bestReplayId={data.premiumBestReplayId}
        bestMedalTier={data.premiumBestMedalTier}
        leaderboardEntries={data.premiumLeaderboard}
        access={data.accessByMapId[data.premiumMapId] ?? { ok: true }}
        dailyStreak={data.premiumDailyStreak}
      />
    </div>
  );
}

function HomePage() {
  const data = Route.useLoaderData();

  return (
    <>
      <PageShell>
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
          {data.latestUpdate && !data.isAnonymous ? (
            <UpdatesBanner
              latestUpdate={data.latestUpdate}
              serverLastSeenUpdateId={data.lastSeenUpdateId}
            />
          ) : null}
          <PageIntro>
            <PageEyeBrow>{homeWelcomeLine(data.isAnonymous, data.displayName)}</PageEyeBrow>
            <PageHeader>{data.todayStr}</PageHeader>
            <Badge variant="default" className="px-4 py-3 text-sm">
              <NewMapsCountdown />
            </Badge>
          </PageIntro>
          {data.showEarlyBirdPromo ? <EarlyBirdPromoCard /> : null}
          {data.showDiscordBanner ? <DiscordPromoCard /> : null}
          <MonthlyHiscoresMarquee entries={data.monthlyHiscoreLeaders} monthKey={data.monthKey} />
          <HomeDailyMapCards data={data} />
          <HomeChallengeSection
            current={data.challenge.current}
            weeklyStreak={data.challenge.weeklyStreak}
          />
          <GlobalStatsSection linkToStats />
          <LearnFromTheBestSection
            boards={data.yesterdayBoards}
            lastWeekChallenge={data.challenge.previous}
          />
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
            <TutorialPromptCard />
            <ArchivePromptCard />
          </div>
        </div>
      </PageShell>
    </>
  );
}

function TutorialPromptCard() {
  return (
    <Card className="h-full gap-3 py-3">
      <CardHeader className="gap-1">
        <CardTitle>
          <SectionHeader>New to Daily Wars?</SectionHeader>
        </CardTitle>
        <CardDescription>
          Check out the tutorial to get to know the basics, or check out the{" "}
          <Link
            to="/codex"
            preload={false}
            className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
          >
            guide
          </Link>{" "}
          for more details.
        </CardDescription>
      </CardHeader>
      <CardFooter className="mt-auto justify-center">
        <Button
          variant="default"
          size="lg"
          className="mx-auto"
          render={<Link to="/tutorial" preload={false} />}
          nativeButton={false}
        >
          Play tutorial →
        </Button>
      </CardFooter>
    </Card>
  );
}

function ArchivePromptCard() {
  return (
    <Card className="h-full gap-3 py-3">
      <CardHeader className="gap-1">
        <CardTitle>
          <SectionHeader>Missed a day?</SectionHeader>
        </CardTitle>
        <CardDescription>Replay past daily maps from the archive.</CardDescription>
      </CardHeader>
      <CardFooter className="mt-auto justify-center">
        <Button
          size="lg"
          className="mx-auto"
          render={<Link to="/archive" preload={false} />}
          nativeButton={false}
        >
          View archive →
        </Button>
      </CardFooter>
    </Card>
  );
}
